import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Scan } from 'lucide-react';
import toast from 'react-hot-toast';
import { useInventory } from '../hooks/useInventory';
import { useSession } from '../hooks/useSession'; 

const ScanUpcPage: React.FC = () => {
  const { tagCode } = useParams<{ tagCode: string }>();
  const navigate = useNavigate();
  const { addItem, isLoading } = useInventory();

  const [upc, setUpc] = useState('');
  const [error, setError] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const { batchId, isSessionActive } = useSession();

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const validateUpc = (value: string): string => {
    return value.trim() ? '' : 'UPC is required';
  };

  const handleSubmit = async () => {
    const trimmedUpc = upc.trim();
    const validationError = validateUpc(trimmedUpc);

    if (validationError) {
      setError(validationError);
      toast.error(validationError);
      return;
    }

    if (!tagCode) {
      toast.error('Tag code is missing');
      return;
    }

    if (!batchId) {
      toast.error('batchId is missing');
      return;
    }

    try {
      await addItem({ batchId: batchId, tagCode, upc: trimmedUpc, count: 1 });
      toast.success('UPC scanned successfully!');
      navigate(`/update-count/${encodeURIComponent(tagCode)}/${encodeURIComponent(trimmedUpc)}`);
    } catch (error) {
      console.error('Error submitting UPC:', error);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-xl bg-white rounded-2xl shadow-lg p-8 border border-gray-200">
        {/* Header */}
        <div className="flex items-center mb-6">
          <button
            onClick={() => navigate(-1)}
            className="p-2 rounded-full hover:bg-gray-100 text-gray-500 transition"
            aria-label="Go back"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <h1 className="ml-3 text-2xl font-semibold text-gray-800">Scan UPC</h1>
        </div>

        {/* Instructions */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 mb-4">
            <Scan className="w-8 h-8 text-green-600" />
          </div>
          <h2 className="text-xl font-medium text-gray-700">Please scan or enter the UPC</h2>
          <p className="mt-2 text-sm text-blue-800 bg-blue-50 inline-block px-3 py-1 rounded-md">
            Tag Code: <span className="font-mono">{tagCode || 'N/A'}</span>
          </p>
        </div>

        {/* Input Field */}
        <div className="space-y-4">
          <div>
            <label htmlFor="upc" className="block text-sm font-medium text-gray-700 mb-1">
              UPC
            </label>
            <input
              ref={inputRef}
              id="upc"
              name="upc"
              type="text"
              value={upc}
              onChange={(e) => {
                setUpc(e.target.value);
                if (error) setError('');
              }}
              onKeyDown={handleKeyPress}
              disabled={isLoading}
              placeholder="Scan or enter UPC"
              autoComplete="off"
              className={`w-full px-4 py-3 text-lg rounded-md shadow-sm border focus:outline-none focus:ring-2 ${
                error
                  ? 'border-red-300 bg-red-50 text-red-800 focus:ring-red-500'
                  : 'border-gray-300 bg-white text-gray-900 focus:ring-green-500'
              } ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
            />
            {error && (
              <p className="mt-1 text-sm text-red-600 flex items-center">
                ⚠️ <span className="ml-1">{error}</span>
              </p>
            )}
          </div>

          {/* Submit Button */}
          <button
            onClick={handleSubmit}
            disabled={isLoading || !upc.trim()}
            className={`w-full py-3 rounded-md text-white font-medium text-sm transition-all focus:outline-none focus:ring-2 focus:ring-offset-1 ${
              isLoading || !upc.trim()
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-green-600 hover:bg-green-700 focus:ring-green-500'
            }`}
          >
            {isLoading ? (
              <div className="flex items-center justify-center gap-2">
                <svg className="animate-spin h-5 w-5 text-white" viewBox="0 0 24 24" fill="none">
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                  />
                </svg>
                Processing...
              </div>
            ) : (
              'Submit UPC'
            )}
          </button>
        </div>

        {/* Footer Quick Actions - Only New Tag */}
        <div className="mt-8 pt-6 border-t border-gray-100">
          <div className="flex justify-center">
            <button
              onClick={() => navigate('/scan-tag')}
              className="text-sm px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md transition"
            >
              New Tag
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ScanUpcPage;